/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { api, ApiError } from "core/api";
import { Alert, Card, Button, SvgIcon, Input, Table, Select } from "core/components";
import { ProjectFormData, ReturnsSchema } from "core/schema";
import { change_order_returns_admin_project_path, inventory_returns_admin_project_path, returns_admin_project_path } from "routes";
import { Controller, useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { yupResolver } from "@hookform/resolvers/yup";

type ProjectReturnFormProps = {
  project: ProjectFormData;
};

const ProjectReturnForm = ({ project, states, source, onCancel, onSubmit }: ProjectReturnFormProps): JSX.Element => {
  const [loading, setLoading] = React.useState(true);
  const [inventoryReturns, setInventoryReturns] = React.useState([]);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isValidating, errors }
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(ReturnsSchema)
  });
  const table = React.useRef(null);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 2,
        Header: "Item ID",
        accessor: "item.code",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 3,
        Header: "Item Name",
        accessor: "item.name",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 4,
        Header: "Task Code",
        accessor: "task.code",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 5,
        Header: "Task Name",
        accessor: "task.name",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Task",
        accessor: "task",
        Cell: (props) => {
          const task = props.value;

          return (
            <>
              <div className="text-bold text-truncate" title={task.task_classification.name} data-bs-toggle="tooltip">
                {`${task.code} - ${task.task_classification.name}`}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={task.name} data-bs-toggle="tooltip">
                {task.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Item",
        accessor: "item",
        Cell: (props) => {
          const item = props.value;
          setValue(`returns.${props.row.index}.item_id`, item.id);
          setValue(`returns.${props.row.index}.project_task_item_id`, props.row.original.id);
          setValue(`returns.${props.row.index}.source`, source);

          return (
            <>
              <div className="text-bold" title={item.code} data-bs-toggle="tooltip">
                {item.code}
              </div>
              <p style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }} title={item.name} data-bs-toggle="tooltip">
                {item.name}
              </p>
            </>
          );
        }
      },
      {
        Header: "Actual Quantity",
        accessor: "actual_quantity",
        Cell: (props) => {
          const actualQuantity = parseFloat(props.value).toLocaleString("en-US", { minimumFractionDigits: 2 });

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{`${actualQuantity} ${props.row.original.unit_measure.code}`}</p>
            </div>
          );
        }
      },
      {
        Header: "Open Return",
        accessor: "open_return_quantity",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Return Quantity",
        accessor: "",
        Cell: (props) => {
          const actualQuantity = parseFloat(props.row.original.actual_quantity);
          const openQuantity = parseFloat(props.row.original.open_return_quantity);
          const remainingQuantity = actualQuantity - openQuantity;
          const canReturn = remainingQuantity > 0;

          return (
            <Controller
              name={`returns.${props.row.index}.returned_quantity`}
              control={control}
              render={({ field, fieldState }) => (
                <IMaskInput
                  mask={Number}
                  padFractionalZeros={true}
                  thousandsSeparator=","
                  radix="."
                  value={field.value?.toString()}
                  unmask={true}
                  inputRef={field.ref}
                  onAccept={(value) => {
                    field.onChange(value);
                  }}
                  className="field"
                  placeholder={canReturn ? "0.0" : "Nothing to return"}
                  min={0.1}
                  max={remainingQuantity}
                  readOnly={!canReturn}
                  style={fieldState.error ? { borderColor: "red" } : {}}
                />
              )}
            />
          );
        }
      },
      {
        Header: "State",
        accessor: "",
        Cell: (props) => {
          const actualQuantity = parseFloat(props.row.original.actual_quantity);
          const openQuantity = parseFloat(props.row.original.open_return_quantity);
          const remainingQuantity = actualQuantity - openQuantity;
          const canReturn = remainingQuantity > 0;

          return (
            <Controller
              name={`returns.${props.row.index}.state`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Select State"
                  isClearable={false}
                  options={states}
                  noOptionsMessage={() => "No states available."}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  onChange={(newOption) => {
                    field.onChange(newOption?.value);
                  }}
                  value={states.find((state) => state.value == field.value) || null}
                  errorMessage={props.errors?.returns?.[props.row.index]?.state?.message}
                  disabled={!canReturn}
                />
              )}
            />
          );
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          const actualQuantity = parseFloat(props.row.original.actual_quantity);
          const openQuantity = parseFloat(props.row.original.open_return_quantity);
          const remainingQuantity = actualQuantity - openQuantity;
          const canReturn = remainingQuantity > 0;

          return <Input {...register(`returns.${props.row.index}.remarks`)} readOnly={!canReturn} />;
        }
      }
    ],
    []
  );

  const getProjectReturns = async (projectId) => {
    try {
      setFormErrors([]);
      setLoading(true);
      const response =
        source === "project_task"
          ? await api.get(returns_admin_project_path(projectId))
          : await api.get(change_order_returns_admin_project_path(projectId));
      setInventoryReturns(response.data);
      setValue("project_id", project.id);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getProjectReturns(project.id).catch(console.error);
  }, []);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading inventory items for return...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No inventory items for return.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Task ID" onChange={(e) => table.current.setFilter(4, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Task Name" onChange={(e) => table.current.setFilter(5, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Inventory ID" onChange={(e) => table.current.setFilter(2, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Inventory Name" onChange={(e) => table.current.setFilter(3, e.target.value)} />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={inventoryReturns} ref={table} hiddenColumns={[2, 3, 4, 5]} setSkipPageReset={true} errors={errors} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return inventoryReturns.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      <>
        <div className="d-flex justify-content-end align-items-baseline">
          <Button
            className="button--default"
            icon="times"
            title="Cancel"
            type="button"
            onClick={() => {
              onCancel();
            }}
          />
        </div>
        {noContent()}
      </>
    );
  };

  const save = async (data) => {
    try {
      setFormErrors([]);
      const inventoryReturns = {
        ...data,
        returns: data.returns.filter((item) => item.returned_quantity)
      };
      await api.post(inventory_returns_admin_project_path(project.id), inventoryReturns);
      onSubmit();
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    if (data?.returns) {
      setFormErrors([data.returns.message]);
    } else {
      setFormErrors(["There are errors on your form."]);
    }
  };

  return (
    <Card.Body>
      <Container fluid={true}>
        {inventoryReturns.length > 0 && (
          <Row className="mb-3">
            <Col lg={6} className="d-flex">
              {formErrors && formErrors.length != 0 && (
                <Alert>
                  {formErrors.map((error, index) => {
                    return <div key={index}>{error}</div>;
                  })}
                </Alert>
              )}
            </Col>
            <Col lg={6} className="d-flex justify-content-end align-items-start">
              <Button
                className="button--default mr-3"
                icon="times"
                title="Cancel"
                type="button"
                onClick={() => {
                  onCancel();
                }}
              />
              <Button
                className="button--brand"
                icon="save"
                title="Submit Returns"
                type="button"
                disabled={isSubmitting || isValidating}
                onClick={() => {
                  handleSubmit(async (data) => await save(data), onError)();
                }}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col>{loading ? loader() : content()}</Col>
        </Row>
      </Container>
    </Card.Body>
  );
};

export default ProjectReturnForm;
